import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from '../../app/router';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { Button, cn, focusRing, IconButton, Input } from '../../ui';
import { Icon } from '../../ui/icons';
import { usePaletteCommands } from '../palette/registry';

/** The board's state shown in the pill (the viewer's role moved beside the avatars, P16-3). */
export type TitleStatus = 'readOnly' | 'offline' | 'local';

const STATUS_CLASS: Record<TitleStatus, string> = {
  readOnly: 'bg-surface-2 text-fg-muted',
  offline: 'bg-warning/15 text-fg',
  local: 'bg-surface-2 text-fg-muted',
};
const DOT_CLASS: Record<TitleStatus, string> = {
  readOnly: 'bg-fg-subtle',
  offline: 'bg-warning',
  local: 'bg-fg-subtle',
};
const MAX_TITLE = 200;

/**
 * The board's title pill (P5d, backlog §10.1), after the main menu: the folder (project, or all boards) in small type
 * above the name (renamed in place when allowed), then the viewer's state, the live sync state and the favorite star.
 * The way back lives in the main menu's File (P5f, backlog §14.2).
 */
export function TitlePill({
  title,
  status,
  onRename,
  folder,
  children,
  favorite,
  badge,
  compact,
}: {
  title: string;
  status?: TitleStatus;
  /** Absent: the name is not editable (viewers, guests, local boards). Rejects when the save fails. */
  onRename?: (title: string) => Promise<void>;
  /** Where the board lives (its project, or all boards for a local board): a small link above the name. */
  /** `onClick` may stop the navigation (unsaved edits ask first, P16-3). */
  folder?: { label: string; to: string; onClick?: (e: MouseEvent<HTMLAnchorElement>) => void };
  /** Live sync state. */
  children?: ReactNode;
  favorite?: { on: boolean; onToggle: () => void };
  badge?: ReactNode;
  /** Phones (P5f): the name alone, the state as a coloured dot, no folder line and no favorite star. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState(false);
  // The main menu's "Rename" and the palette land here.
  usePaletteCommands(() =>
    onRename ? [{ id: 'rename', label: t('mainMenu.rename'), group: 'board', keywords: ['title', 'titre', 'name', 'nom'], run: () => setEditing(true) }] : [],
  );

  const commit = (value: string) => {
    const next = value.trim().slice(0, MAX_TITLE);
    if (!onRename || !next || next === title) return setEditing(false);
    onRename(next).then(
      () => {
        setFailed(false);
        setEditing(false);
      },
      () => setFailed(true),
    );
  };

  return (
    <>
      <div data-title-stack="" className="flex min-w-0 flex-col items-start justify-center leading-tight pointer-coarse:flex-row pointer-coarse:items-center">
        {folder && !compact && (
          <Link
            to={folder.to}
            onClick={folder.onClick}
            className={cn('max-w-[40vw] truncate rounded px-1.5 text-[11px] font-medium text-fg-muted hover:text-fg pointer-coarse:flex pointer-coarse:h-11 pointer-coarse:max-w-[20vw] pointer-coarse:items-center pointer-coarse:text-sm', focusRing)}
          >
            {folder.label}
          </Link>
        )}
        {/* Touch: folder and name share one line (a 44 px link), with a slash between them. */}
        {folder && !compact && (
          <span aria-hidden="true" className="hidden text-fg-subtle pointer-coarse:inline">
            /
          </span>
        )}
        {editing && onRename ? (
          <Input
            aria-label={t('titlePill.name')}
            defaultValue={title}
            maxLength={MAX_TITLE}
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit(e.currentTarget.value);
              else if (e.key === 'Escape') {
                e.preventDefault();
                setFailed(false);
                setEditing(false);
              }
            }}
            onBlur={(e) => commit(e.currentTarget.value)}
            className="h-8 w-[min(40vw,18rem)] px-2 font-display text-base font-semibold"
          />
        ) : onRename ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('titlePill.rename', { title })}
            title={t('mainMenu.rename')}
            onClick={() => setEditing(true)}
            className="h-auto max-w-[40vw] px-1.5 py-0.5 font-display text-base font-semibold"
          >
            <span className="truncate">{title}</span>
          </Button>
        ) : (
          <span className="max-w-[40vw] truncate px-1 font-display text-base font-semibold" title={title}>
            {title}
          </span>
        )}
      </div>
      {failed && (
        <span role="alert" className="text-xs text-danger">
          {t('titlePill.renameFailed')}
        </span>
      )}
      {!status ? null : compact ? (
        <span
          role="img"
          data-status={status}
          aria-label={t(`titlePill.status.${status}`)}
          title={t(`titlePill.status.${status}`)}
          className={cn('size-2.5 shrink-0 rounded-full', DOT_CLASS[status])}
        />
      ) : (
        <span data-status={status} className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', STATUS_CLASS[status])}>
          {t(`titlePill.status.${status}`)}
        </span>
      )}
      {badge}
      {children}
      {favorite && !compact && (
        <IconButton
          pressed={favorite.on}
          label={favorite.on ? t('dashboard.unfavorite') : t('dashboard.favorite')}
          onClick={favorite.onToggle}
          icon={<Icon icon={Star} size={16} className={favorite.on ? 'fill-current' : undefined} />}
          className="h-8 w-8 rounded-full aria-pressed:bg-transparent aria-pressed:text-accent"
        />
      )}
    </>
  );
}
